import { GenericMenuItemInfo } from '@tableau/widgets';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { CreateExplorerEvent } from '../../../common/telemetryEvents/TelemetryEventCreator';
import { ExplorerDisplayMode, TelemetryEvents } from '../../../common/telemetryEvents/TelemetryEvents';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { DisplayMode } from '../../../ts/ServerApi/types/ISessionInfo';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { ExplorerDropdown } from '../Components/ExplorerDropdown';
import {
  ExplorerDropdownItemHeaderFormatter,
  ExplorerDropdownItemImageFormatter,
} from '../Components/ExplorerDropdownPanelItems';
import { ExplorerFlyoutPanelProps } from '../Components/ExplorerFlyoutPanel';
import { ExplorerListMenuPanel, ExplorerListMenuPanelProps } from '../Components/ExplorerListMenuPanel';
import { ExplorerStyles } from './ExplorerStyles';
import { ExplorerToolbarTestIds } from './ExplorerToolbar';

export namespace ExplorerViewModeSwitcherTestIds {
  export const CardButton = 'view-mode-toggle-thumbnail';
  export const ListButton = 'view-mode-toggle-list';
  export const MainButton = 'view-mode-toggle';
}

interface ViewModeInfo {
  mode: DisplayMode;
  imgUrl: string;
  imgSelectedUrl: string;
  imgDisabledUrl: string;
  testId: string;
  displayValue: () => string;
}

const ViewModeInfos: ViewModeInfo[] = [
  {
    mode: 'thumbnail',
    imgUrl: imgPath('explorer_display_view_mode_thumbnail.svg'),
    imgSelectedUrl: imgPath('explorer_display_view_mode_thumbnail_selected.svg'),
    imgDisabledUrl: imgPath('explorer_display_view_mode_thumbnail_disabled.svg'),
    testId: ExplorerViewModeSwitcherTestIds.CardButton,
    displayValue: Messages.explorerViewMode_thumbnail
  },
  {
    mode: 'list',
    imgUrl: imgPath('explorer_display_view_mode_list.svg'),
    imgSelectedUrl: imgPath('explorer_display_view_mode_list_selected.svg'),
    imgDisabledUrl: imgPath('explorer_display_view_mode_list_disabled.svg'),
    testId: ExplorerViewModeSwitcherTestIds.ListButton,
    displayValue: Messages.explorerViewMode_list
  }
];

export interface ExplorerViewModeSwitcherProps {
  readonly currentViewMode: DisplayMode;
  readonly changeViewMode: (viewMode: DisplayMode) => void;
  readonly telemetry: Telemetry;
  readonly disabled: boolean;
}

export class ExplorerViewModeSwitcher extends React.PureComponent<ExplorerViewModeSwitcherProps> {

  private changeViewMode = (mode: DisplayMode): void => {
    this.props.changeViewMode(mode);

    // we want to send this event even if the mode didn't change-- that way we can see if users
    // are confused and click on the current mode anyway
    this.props.telemetry.sendEvent<ExplorerDisplayMode>(CreateExplorerEvent<ExplorerDisplayMode>(
      {
        type: TelemetryEvents.ExplorerDisplayMode,
        previousMode: this.props.currentViewMode,
        newMode: mode
      }
    ));
  }

  private currentModeImgUrl = (): ViewModeInfo => {
    return _.find(ViewModeInfos, (info) => info.mode === this.props.currentViewMode);
  }

  private renderPanel = (panelProps: ExplorerFlyoutPanelProps): JSX.Element => {

    let menuItems: GenericMenuItemInfo[] = [];

    menuItems = menuItems.concat(ViewModeInfos.map((info: ViewModeInfo) => {
      return {
        value: info.mode,
        disabled: false,
        checked: info.mode === this.props.currentViewMode,
        displayFormatterBoundWithData: ExplorerDropdownItemImageFormatter(info.displayValue(), info.imgUrl),
        keyboardAccessibleValue: info.displayValue()
      } as GenericMenuItemInfo;
    }));

    const props: ExplorerListMenuPanelProps = {
      ...panelProps,
      menuItems,
      onSelect: (value: string) => {
        panelProps.onCloseRequest();
        this.changeViewMode(value as DisplayMode);
      },
      onComponentDidUpdate: _.noop,
      width: 150,
      id: `${panelProps.testId}-${ExplorerToolbarTestIds.ExplorerDropdownLabelSuffix}`,
      role: 'menu'
    };

    return <ExplorerListMenuPanel {...props} />;
  }

  private renderButtonContent = (_showingOrActive: boolean): JSX.Element => {
    const currentViewMode = this.currentModeImgUrl();
    return (
      <>
        {currentViewMode &&
          <img
            src={this.props.disabled ? currentViewMode.imgDisabledUrl : currentViewMode.imgUrl}
            className={ExplorerStyles.ExplorerViewModeMainButtonImg}
          />
        }
      </>
    );
  }

  public render(): JSX.Element {
    return (
      <ExplorerDropdown
        disabled={this.props.disabled}
        buttonTitle={Messages.explorerViewMode()}
        testId={ExplorerViewModeSwitcherTestIds.MainButton}
        renderButtonContent={this.renderButtonContent}
        renderPanel={this.renderPanel}
        buttonStyleOverrides={{minWidth: 0, height: 'auto'}}
        panelTitle={Messages.explorerViewMode()}
      />
    );
  }
}
